// Public GDPR data-deletion request page (/data-deletion). Linked from the
// privacy policy's deletion box; no login. Submitting sends a confirmation
// link to the given address — the request stays inactive until it is clicked.
import ArrowBack from "@mui/icons-material/ArrowBack";
import MarkEmailReadOutlined from "@mui/icons-material/MarkEmailReadOutlined";
import {
	Alert,
	Box,
	Button,
	Container,
	Link,
	Paper,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";

interface DeletionRequestForm {
	email: string;
	name: string;
	linkedin_url: string;
	phone: string;
	details: string;
}

const emptyForm: DeletionRequestForm = {
	email: "",
	name: "",
	linkedin_url: "",
	phone: "",
	details: "",
};

export const DataDeletionRequestPage: React.FC = () => {
	const [form, setForm] = useState<DeletionRequestForm>(emptyForm);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [sentTo, setSentTo] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	const setField =
		(field: keyof DeletionRequestForm) =>
		(event: React.ChangeEvent<HTMLInputElement>) =>
			setForm({ ...form, [field]: event.target.value });

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setIsSubmitting(true);
		setError(null);
		try {
			const response = await fetch("/api/v2/deletion-request/", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(form),
			});
			if (!response.ok) {
				throw new Error(`${response.status} ${response.statusText}`);
			}
			setSentTo(form.email);
		} catch (err) {
			setError(
				`Could not submit the request: ${err instanceof Error ? err.message : String(err)}`
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Box sx={{ minHeight: "100vh", bgcolor: "grey.50", py: { xs: 3, sm: 6 } }}>
			<Container component="main" maxWidth="sm">
				<Link
					component={RouterLink}
					to="/privacy-policy"
					sx={{
						display: "inline-flex",
						alignItems: "center",
						color: "primary.main",
						textDecoration: "none",
						fontSize: "0.875rem",
						fontWeight: 500,
						mb: 3,
						"&:hover": { textDecoration: "underline" },
					}}
					data-component="deletion-request-back-link"
				>
					<ArrowBack sx={{ fontSize: 16, mr: 1 }} />
					Back to Privacy Policy
				</Link>

				{sentTo === null ? (
					<Paper
						data-component="deletion-request-form"
						elevation={0}
						sx={{
							p: { xs: 3, sm: 4 },
							borderRadius: 2,
							boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
						}}
					>
						<Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
							Request deletion of your data
						</Typography>
						<Typography
							variant="body2"
							color="text.secondary"
							sx={{ mb: 3 }}
						>
							If your data appears in our recruitment database you can ask
							for it to be removed. We first send a confirmation link to
							your email address; once you confirm, an operator reviews
							the request. Your data is then deleted and never collected
							again, and you receive an email when it is done.
						</Typography>

						{error && (
							<Alert severity="error" sx={{ mb: 2 }}>
								{error}
							</Alert>
						)}

						<form onSubmit={handleSubmit}>
							<Stack spacing={2}>
								<TextField
									data-component="deletion-request-email"
									label="Your email address"
									type="email"
									required
									value={form.email}
									onChange={setField("email")}
									helperText="The confirmation link and the final notice are sent here"
									fullWidth
								/>
								<TextField
									data-component="deletion-request-name"
									label="Your full name"
									required
									value={form.name}
									onChange={setField("name")}
									fullWidth
								/>
								<TextField
									data-component="deletion-request-linkedin-url"
									label="Your LinkedIn profile URL"
									placeholder="https://www.linkedin.com/in/..."
									value={form.linkedin_url}
									onChange={setField("linkedin_url")}
									helperText="Optional, but the fastest way for us to find the right profile"
									fullWidth
								/>
								<TextField
									data-component="deletion-request-phone"
									label="Your phone number"
									value={form.phone}
									onChange={setField("phone")}
									helperText="Optional — helps us match the request to the right person"
									fullWidth
								/>
								<TextField
									data-component="deletion-request-details"
									label="Anything else that identifies you"
									multiline
									minRows={3}
									value={form.details}
									onChange={setField("details")}
									helperText="Optional — e.g. current employer or city, if your name is common"
									fullWidth
								/>
								<Button
									data-component="deletion-request-submit"
									type="submit"
									variant="contained"
									size="large"
									disabled={isSubmitting}
									sx={{ textTransform: "none", fontWeight: 500 }}
								>
									Send confirmation link
								</Button>
							</Stack>
						</form>
					</Paper>
				) : (
					<Paper
						data-component="deletion-request-sent"
						elevation={0}
						sx={{
							p: { xs: 3, sm: 4 },
							borderRadius: 2,
							textAlign: "center",
							border: "2px solid",
							borderColor: "primary.main",
						}}
					>
						<MarkEmailReadOutlined
							sx={{ fontSize: 48, color: "primary.main", mb: 2 }}
						/>
						<Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
							Confirm your email address
						</Typography>
						<Typography variant="body1" color="text.secondary">
							We sent a confirmation link to <strong>{sentTo}</strong>.
							Click the link to confirm your request — the confirmation
							page shows your reference number, and you will get an email
							when the deletion is complete.
						</Typography>
					</Paper>
				)}
			</Container>
		</Box>
	);
};
